import {
    STOREFRONT_PATH_PREFIXES,
    categoryIdHref,
    categoryPath,
    searchHref,
} from "@/shared/routes";
import type { ResolvedLink, WidgetSearchRules } from "../types/widget-settings";
import type { Category } from "@/modules/categories";

// Re-export: consumidores antigos importavam os builders de categoria daqui.
// A fonte canônica é @/shared/routes — prefira importar de lá.
export { categoryPath, categoryIdHref };
/** @deprecated use categoryIdHref (mesma função, nome alinhado ao módulo de rotas). */


/** true quando o caminho começa por uma rota REAL do storefront. */
function isKnownStorefrontPath(path: string): boolean {
    return STOREFRONT_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/**
 * Resolve URLs de widget que podem vir como deep links (legado mobile).
 * URLs absolutas e rotas conhecidas do app passam direto; rotas desconhecidas
 * viram busca pelo texto do último segmento (comportamento herdado dos deep
 * links do app — ver nota em resolveCategoryHref).
 */
export function resolveWidgetUrl(url: string): string {
    if (!url) return searchHref();
    if (url.startsWith("http")) return url;
    if (isKnownStorefrontPath(url)) return url;

    const slug = url.split("/").filter(Boolean).pop() ?? "";
    return searchHref(slug.replace(/-/g, " "));
}

/**
 * Traduz o `link` gravado pelo portal para uma rota do storefront.
 *
 *  - "#" ou vazio        → sem ação (null; o widget não vira link)
 *  - "categoria/{id}"    → "/categoria/{id}" (a página redireciona pro canônico de slugs)
 *  - "page=search-page"  → "/busca" (com keyword de searchRules, se houver)
 *  - URL absoluta        → mantida
 *  - qualquer outro      → resolveWidgetUrl (deep link legado)
 */
export function resolveWidgetLink(
    link: string | undefined | null,
    searchRules?: WidgetSearchRules | null
): string | null {
    if (!link || link === "#") return null;

    if (link.startsWith("categoria/")) {
        const id = link.slice("categoria/".length);
        return id ? categoryIdHref(id) : null;
    }

    if (link === "page=search-page") {
        return searchHref(searchRules?.keyword);
    }

    if (link.startsWith("http")) return link;

    return resolveWidgetUrl(link);
}

/**
 * Href de um item de widget: prefere o `linkHref` RESOLVIDO PELO BACKEND
 * (contrato novo — URL pronta, null = sem ação) e só cai no parsing local de
 * `link` quando a resposta veio de uma API antiga, sem o campo.
 */
export function widgetLinkHref(
    item: ResolvedLink,
    link?: string | null,
    searchRules?: WidgetSearchRules | null
): string | null {
    if (item.linkHref !== undefined) return item.linkHref;
    return resolveWidgetLink(link, searchRules);
}

/**
 * Href de uma categoria: SEMPRE a listagem canônica hierárquica
 * (/categoria/{slug-pai}/{slug}). `parentNames` é a trilha de nomes até o pai
 * (vazia para categorias raiz).
 *
 * O clickAction.redirect do portal só é honrado quando aponta explicitamente
 * para uma rota conhecida do storefront ou URL absoluta. Deep links de slug
 * (legado mobile, ex. "pizzas/doces") NÃO caem no fallback de busca por texto
 * do resolveWidgetUrl: buscar pelo label é outra semântica — retorna qualquer
 * produto com a palavra, ignorando a hierarquia de categorias.
 */
export function resolveCategoryHref(
    category: Pick<Category, "ecmCategoryId" | "name" | "clickAction">,
    parentNames: string[] = []
): string {
    const redirect = category.clickAction?.redirect;

    if (redirect) {
        if (redirect.startsWith("categoria/")) {
            const id = redirect.slice("categoria/".length);
            if (id) return categoryIdHref(id);
        }

        if (redirect.startsWith("http") || isKnownStorefrontPath(redirect)) {
            return redirect;
        }
    }

    return categoryPath([...parentNames, category.name]);
}
